import React, { useState, useContext } from "react";
import { InterviewContext } from "../../../contexts/InterviewContext";
import { Button } from "../../../components/ui/Button";
import { Info, AlertTriangle } from "lucide-react";
import FallbackConfirmationDialog from "./dialogs/FallbackConfirmationDialog";
import FallbackInformationDialog from "./dialogs/FallbackInformationDialog";

interface FallbackInformationProps {
  className?: string;
}

interface FallbackInfoItem {
  content: string;
  graphTitle: string;
  graphUrl: string;
  id: number;
  needFallback: boolean;
}

const FallbackInformation: React.FC<FallbackInformationProps> = ({
  className,
}) => {
  const { fallbackInformation } = useContext(InterviewContext)!;
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showInformationDialog, setShowInformationDialog] = useState(false);

  // Parse fallback information if it exists
  const parsedFallbackInfo: FallbackInfoItem[] = React.useMemo(() => {
    if (!fallbackInformation || fallbackInformation.length === 0) {
      return [];
    }

    try {
      let parsedData: any = fallbackInformation;

      // Try to parse as JSON if it's a string
      if (typeof fallbackInformation === "string") {
        parsedData = JSON.parse(fallbackInformation);
      }

      // If it's already an array, return as is
      if (Array.isArray(parsedData)) {
        return parsedData;
      }

      return [];
    } catch (error) {
      console.error("Error parsing fallback information:", error);
      return [];
    }
  }, [fallbackInformation]);

  const handleShowInformation = () => {
    setShowConfirmationDialog(true);
  };

  const handleConfirmationYes = () => {
    setShowConfirmationDialog(false);
    setShowInformationDialog(true);
  };

  const handleConfirmationNo = () => {
    setShowConfirmationDialog(false);
  };

  const handleCloseInformationDialog = () => {
    setShowInformationDialog(false);
  };

  // Handle direct open change from dialogs
  const handleConfirmationOpenChange = (open: boolean) => {
    setShowConfirmationDialog(open);
  };

  const handleInformationOpenChange = (open: boolean) => {
    setShowInformationDialog(open);
  };

  // Don't render if no fallback information is available
  if (parsedFallbackInfo.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          onClick={handleShowInformation}
          size="sm"
          className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
        >
          <Info className="h-4 w-4" />
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <FallbackConfirmationDialog
        isOpen={showConfirmationDialog}
        onOpenChange={handleConfirmationOpenChange}
        onConfirm={handleConfirmationYes}
        onCancel={handleConfirmationNo}
      />

      {/* Information Display Dialog */}
      <FallbackInformationDialog
        isOpen={showInformationDialog}
        onOpenChange={handleInformationOpenChange}
        fallbackInfo={parsedFallbackInfo}
        onClose={handleCloseInformationDialog}
      />
    </>
  );
};

export default FallbackInformation;
